// src/pages/TenantDetailPage.jsx
import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { useTenants } from '../hooks/useTenants';
import RiskBadge from '../components/RiskBadge';
import MessageModal from '../components/MessageModal';
import PaymentPlanModal from '../components/PaymentPlanModal';
import MarkPaidModal from '../components/MarkPaidModal';
import { formatCurrency, formatDate } from '../utils/formatters';
import { exportOutreachLog, exportPaymentHistory } from '../utils/exportUtils';
import { analyzeSentiment, SENTIMENT_CONFIG, LIKELIHOOD_CONFIG } from '../utils/sentimentAnalysis';
import { calculateERV, flattenOutreachLogs } from '../utils/ervEngine';
import { sendTenantSms } from '../utils/outreach';

const FACTOR_LABELS = {
  daysLate: { label: 'Days Late (Current)', weight: '35%' },
  lateFrequency: { label: 'Late Frequency', weight: '25%' },
  balanceRatio: { label: 'Balance vs. Rent', weight: '20%' },
  drift: { label: 'Drift Velocity', weight: '15%' },
  partialPayments: { label: 'Partial Payments', weight: '5%' },
};

function StatCard({ label, value, sub, variant = 'neutral' }) {
  const bg = {
    neutral: 'bg-gray-50',
    danger: 'bg-red-50',
    success: 'bg-green-50',
    warning: 'bg-amber-50',
  }[variant];
  const text = {
    neutral: 'text-gray-900',
    danger: 'text-red-600',
    success: 'text-green-700',
    warning: 'text-amber-700',
  }[variant];
  return (
    <div className={"rounded-xl p-4 " + bg}>
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className={"text-xl font-bold mt-1 " + text}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function PaymentStatusBadge({ payment, monthlyRent }) {
  const { daysLate, amount } = payment;
  const isPartial = amount > 0 && amount < (monthlyRent || 0) * 0.95;
  const isMissed = amount === 0;
  if (isMissed) return <span className="text-red-600 font-bold text-xs">Missed</span>;
  if (isPartial) return <span className="text-yellow-600 font-medium text-xs">Partial</span>;
  if (daysLate > 10) return <span className="text-red-500 font-medium text-xs">Very Late</span>;
  if (daysLate > 3) return <span className="text-orange-500 font-medium text-xs">Late</span>;
  return <span className="text-green-600 font-medium text-xs">On Time</span>;
}

export default function TenantDetailPage() {
  const { propertyId, tenantId } = useParams();
  const { user } = useAuth();
  const { tenants, loading, recordPayment } = useTenants(propertyId);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsMode, setSmsMode] = useState('sms');
  const [planOpen, setPlanOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState({ maxInstallments: 3, minPaymentAmount: 100 });
  const [propertyName, setPropertyName] = useState('your property management team');

  // Load user's payment plan settings once
  React.useEffect(() => {
    if (!user?.uid) return;
    getDoc(doc(db, 'users', user.uid, 'settings', 'preferences'))
      .then(snap => {
        if (snap.exists()) {
          const d = snap.data();
          setPaymentSettings({
            maxInstallments: d.maxInstallments || 3,
            minPaymentAmount: d.minPaymentAmount || 100,
          });
        }
      })
      .catch(() => {}); // silently fall back to defaults
  }, [user?.uid]);

  React.useEffect(() => {
    if (!propertyId) return;
    getDoc(doc(db, 'properties', propertyId))
      .then((snap) => {
        if (snap.exists()) {
          setPropertyName(snap.data()?.name || 'your property management team');
        }
      })
      .catch(() => {});
  }, [propertyId]);

  const tenant = tenants.find(t => t.id === tenantId);

  // Compute ERV + sentiment for this tenant
  const erv = useMemo(() => {
    if (!tenant) return null;
    const outreachLogs = flattenOutreachLogs([tenant]);
    return calculateERV(tenant, outreachLogs, new Map());
  }, [tenant]);

  const handleSendSMS = async (t, message, templateUsed, lang) => {
    await sendTenantSms({
      tenant: t,
      propertyId,
      message,
      templateKey: templateUsed,
      lang,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-tenafi-green border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-3">Search</div>
          <p className="text-gray-500 mb-3">Tenant not found.</p>
          <Link to="/" className="text-tenafi-green text-sm font-medium hover:underline">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const driftText = {
    worsening: '↗ Worsening',
    slightly_worsening: '↗ Drifting',
    improving: '↘ Improving',
    slightly_improving: '↘ Getting Better',
    stable: '→ Stable',
    insufficient_data: '— Insufficient Data',
  }[tenant.drift?.direction] || '— Unknown';

  const driftVariant = tenant.drift?.direction?.includes('worsening') ? 'danger'
    : tenant.drift?.direction?.includes('improving') ? 'success' : 'neutral';

  const consistencyLabel = {
    very_consistent: 'Very Consistent',
    consistent: 'Consistent',
    variable: 'Variable',
    unpredictable: 'Unpredictable',
    insufficient_data: 'Insufficient Data',
  }[tenant.fingerprint?.consistency] || 'Unknown';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-tenafi-green text-sm font-medium hover:underline mb-6">
          Back to Action Feed
        </Link>

        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900">
                  {tenant.name}
                  {tenant.preferredLanguage === 'es' && (
                    <span className="ml-2 text-sm font-normal bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full">🇲🇽 Spanish preferred</span>
                  )}
                </h1>
              </div>
              <p className="text-gray-500 text-sm mt-1.5">
                Unit {tenant.unit}
                {tenant.phone && <> · <a href={"tel:" + tenant.phone} className="hover:text-tenafi-green">{tenant.phone}</a></>}
                {tenant.email && <> · <a href={"mailto:" + tenant.email} className="hover:text-tenafi-green">{tenant.email}</a></>}
              </p>
            </div>
            <RiskBadge score={tenant.risk?.score} tier={tenant.risk?.tier} label={tenant.risk?.label} size="lg" />
          </div>

          {/* 4 stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <StatCard label="Monthly Rent" value={formatCurrency(tenant.monthlyRent)} />
            <StatCard label="Current Balance" value={formatCurrency(tenant.currentBalance)}
              variant={tenant.currentBalance > 0 ? 'danger' : 'success'}
              sub={tenant.currentBalance > 0 ? tenant.currentDaysLate + ' days overdue' : 'Paid up'} />
            <StatCard label="Expected Pay Date"
              value={tenant.fingerprint?.expectedNextPayDate
                ? formatDate(tenant.fingerprint.expectedNextPayDate, 'compact') : 'N/A'}
              sub={tenant.fingerprint?.medianPayDay ? 'Day ' + tenant.fingerprint.medianPayDay + ' typical' : null} />
            <StatCard label="Drift" value={driftText} variant={driftVariant}
              sub={tenant.drift?.velocity !== 0 ? Math.abs(tenant.drift?.velocity || 0) + ' days/mo' : null} />
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-gray-100">
            <button onClick={() => { setSmsMode('sms'); setSmsOpen(true); }}
              className="px-4 py-2 bg-tenafi-green text-white rounded-xl text-sm font-medium hover:bg-tenafi-green-dark transition-colors">
              📨 Send Message
            </button>
            {tenant.currentBalance > 0 && (
              <>
                <button onClick={() => setPlanOpen(true)}
                  className="px-4 py-2 border border-tenafi-green text-tenafi-green rounded-xl text-sm font-medium hover:bg-tenafi-light transition-colors">
                  💳 Payment Plan
                </button>
                <button onClick={() => setMarkPaidOpen(true)}
                  className="px-4 py-2 border border-green-300 text-green-700 rounded-xl text-sm font-medium hover:bg-green-50 transition-colors">
                  ✅ Mark as Paid
                </button>
              </>
            )}
          </div>
        </div>

        {/* ERV Panel */}
        {erv && erv.outstandingBalance > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-semibold text-gray-900">Expected Recovery Value</h2>
                <p className="text-xs text-gray-400 mt-0.5">AI probability-weighted estimate of recoverable balance</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-tenafi-green">{formatCurrency(erv.ervDollars)}</p>
                <p className="text-xs text-gray-400">{erv.recoveryProbability}% recovery probability</p>
              </div>
            </div>
            {/* Probability bar */}
            <div className="mb-4">
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${erv.recoveryProbability}%`, backgroundColor: erv.recoveryProbability >= 70 ? '#0F6E56' : erv.recoveryProbability >= 40 ? '#EF9F27' : '#ef4444' }} />
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>0%</span><span>50%</span><span>100%</span>
              </div>
            </div>
            {/* Recommended action */}
            <div className="rounded-xl bg-blue-50 border border-blue-200/60 p-3 mb-3">
              <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-1">🎯 Recommended Action</p>
              <p className="text-xs text-blue-900">{erv.recommendedAction}</p>
            </div>
            {/* Factor pills */}
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Contributing Factors</p>
              <div className="flex flex-wrap gap-1.5">
                {erv.factors.map((f, i) => {
                  const styles = { positive: 'bg-green-50 text-green-700 border-green-200', negative: 'bg-red-50 text-red-600 border-red-200', neutral: 'bg-gray-50 text-gray-500 border-gray-200' };
                  return (
                    <span key={i} title={f.description}
                      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium border cursor-help ${styles[f.impact] || styles.neutral}`}>
                      {f.impact === 'positive' ? '↑' : f.impact === 'negative' ? '↓' : '·'} {f.label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Risk Score Breakdown */}
        {tenant.risk?.factors && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4">Risk Score Breakdown</h2>
            <div className="space-y-4">
              {Object.entries(tenant.risk.factors).map(([key, value]) => {
                const meta = FACTOR_LABELS[key];
                const barColor = value > 70 ? 'bg-red-500' : value > 40 ? 'bg-amber-500' : 'bg-green-500';
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">{meta?.label || key}</span>
                        <span className="text-xs text-gray-400">({meta?.weight || '?'})</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">{value}</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={"h-full rounded-full transition-all " + barColor}
                        style={{ width: Math.min(value, 100) + '%' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Payment History */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Payment History</h2>
            <button onClick={() => exportPaymentHistory(tenant)}
              className="text-xs text-tenafi-green hover:underline font-medium">
              ↓ Export CSV
            </button>
          </div>
          {tenant.paymentHistory?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    <th className="py-2.5 px-3 text-gray-500 font-medium">Date</th>
                    <th className="py-2.5 px-3 text-gray-500 font-medium text-right">Amount Paid</th>
                    <th className="py-2.5 px-3 text-gray-500 font-medium text-right">Days Late</th>
                    <th className="py-2.5 px-3 text-gray-500 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[...tenant.paymentHistory].reverse().map((p, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2.5 px-3 text-gray-600">{formatDate(p.date)}</td>
                      <td className="py-2.5 px-3 font-medium text-right">{formatCurrency(p.amount)}</td>
                      <td className={"py-2.5 px-3 text-right " + (p.daysLate > 5 ? 'text-red-500 font-medium' : 'text-gray-500')}>
                        {p.daysLate || 0}
                      </td>
                      <td className="py-2.5 px-3">
                        <PaymentStatusBadge payment={p} monthlyRent={tenant.monthlyRent} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No payment history on record.</p>
          )}
        </div>

        {/* Payment Fingerprint */}
        {tenant.fingerprint && tenant.fingerprint.averagePayDay && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4">Payment Fingerprint</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              {[
                { label: 'Average Pay Day', value: 'Day ' + tenant.fingerprint.averagePayDay },
                { label: 'Median Pay Day', value: 'Day ' + (tenant.fingerprint.medianPayDay || '?') },
                { label: 'Consistency', value: consistencyLabel },
                { label: 'Pay Cycle', value: tenant.fingerprint.payCycle || 'Unknown' },
                { label: 'Std. Deviation', value: (tenant.fingerprint.stdDev || 0) + ' days' },
                { label: 'Expected Next Payment', value: tenant.fingerprint.expectedNextPayDate ? formatDate(tenant.fingerprint.expectedNextPayDate, 'compact') : 'N/A' },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 font-medium">{label}</p>
                  <p className="font-semibold text-gray-800 mt-0.5">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Outreach Log */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Outreach Log</h2>
            <button onClick={() => exportOutreachLog(tenant)}
              className="text-xs text-tenafi-green hover:underline font-medium">
              ↓ Export CSV
            </button>
          </div>
          {tenant.outreachLog?.length > 0 ? (
            <div className="space-y-3">
              {[...tenant.outreachLog].reverse().map((entry, i) => {
                // Analyze sentiment if the entry has a tenant response
                const sentiment = entry.responseText
                  ? analyzeSentiment(entry.responseText, { daysLate: tenant.currentDaysLate, riskScore: tenant.risk?.score })
                  : null;
                const sentConfig = sentiment ? SENTIMENT_CONFIG[sentiment.sentiment] : null;
                return (
                  <div key={i} className="text-sm rounded-xl bg-gray-50 overflow-hidden">
                    {/* Main log row */}
                    <div className="flex items-start gap-3 p-3">
                      <div className="shrink-0">
                        <span className="text-xs bg-tenafi-light text-tenafi-green px-2 py-0.5 rounded font-medium uppercase">
                          {entry.channel}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(entry.date)}</p>
                      </div>
                      <p className="text-gray-600 flex-1 leading-relaxed">{entry.message}</p>
                      <span className={"text-xs px-1.5 py-0.5 rounded " +
                        (entry.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                        {entry.status}
                      </span>
                    </div>
                    {/* Tenant response + sentiment */}
                    {entry.responseText && (
                      <div className={`mx-3 mb-3 rounded-lg p-2.5 border ${sentConfig?.bg || 'bg-gray-50'} ${sentConfig?.border || 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tenant Reply</p>
                          {sentConfig && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${sentConfig.bg} ${sentConfig.text} ${sentConfig.border} border`}>
                              {sentConfig.icon} {sentConfig.label}
                            </span>
                          )}
                          {sentiment && (
                            <span className={`text-[10px] font-medium ml-auto ${LIKELIHOOD_CONFIG[sentiment.paymentLikelihood]?.color}`}>
                              Pay likelihood: {LIKELIHOOD_CONFIG[sentiment.paymentLikelihood]?.label}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-700 italic">"{entry.responseText}"</p>
                        {sentiment && (
                          <p className="text-[10px] text-gray-500 mt-1">{sentiment.summary}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <div className="text-3xl mb-2">📭</div>
              <p className="text-sm">No outreach recorded yet.</p>
            </div>
          )}
        </div>

        {/* Active Payment Plan card */}
        {tenant.paymentPlan?.status === 'active' && (
        <div className="bg-white rounded-2xl border border-tenafi-green/30 p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-semibold text-gray-900">💳 Active Payment Plan</h2>
              <p className="text-xs text-gray-400 mt-0.5">Created {formatDate(tenant.paymentPlan.createdAt)}</p>
            </div>
            <span className="text-xs bg-tenafi-light text-tenafi-green px-2.5 py-1 rounded-full font-semibold">Active</span>
          </div>
          <div className="rounded-xl border border-gray-100 overflow-hidden">
            {(tenant.paymentPlan.installments || []).map((inst, i) => (
              <div key={i}
                className={`flex items-center justify-between px-4 py-2.5 text-sm ${i < tenant.paymentPlan.installments.length - 1 ? 'border-b border-gray-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                    inst.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>{inst.installment}</span>
                  <span className="text-gray-500">{inst.dueDate}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-800">{formatCurrency(inst.amount)}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    inst.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>{inst.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>

      {smsOpen && (
        <MessageModal tenant={tenant} initialMode={smsMode}
          propertyName={propertyName}
          onClose={() => setSmsOpen(false)} onSend={handleSendSMS} />
      )}

      {planOpen && (
        <PaymentPlanModal
          tenant={tenant}
          propertyId={propertyId}
          maxInstallments={paymentSettings.maxInstallments}
          minPaymentAmount={paymentSettings.minPaymentAmount}
          onClose={() => setPlanOpen(false)}
          onSaved={() => setPlanOpen(false)}
        />
      )}

      {markPaidOpen && (
        <MarkPaidModal
          tenant={tenant}
          propertyId={propertyId}
          onClose={() => setMarkPaidOpen(false)}
          onRecorded={async (payment) => {
            await recordPayment(tenant.id, payment);
          }}
        />
      )}
    </div>
  );
}
